import i18nHandle from '../locales'
import { hasPermission } from './permission'
import { hasSetting } from './setting'
import {
  MenuItemType,
  FormatedMenuItemType,
  MenuRoleFuncOptions,
  UnreadMap,
  TotalMap,
  TodoTaskMap,
} from '@/layout/types'
import { shellConfig } from '@/shell-config'
import { HOSTS } from '@/constants/environment'
import { isEqual } from 'lodash'
import { nextTick } from '@vue/composition-api'

interface FilterMenuListOptions extends MenuRoleFuncOptions {
  menuList: MenuItemType[]
}

// 过滤无权限菜单，然后过滤空的父菜单
export const filterMenuList = (options: FilterMenuListOptions) => {
  const { menuList, privilege, setting } = options
  // 鉴权
  const checkPermission = (menu: MenuItemType) => {
    const { subMenu, roles } = menu

    const withPermission = typeof roles === 'function' ? roles(options) : hasPermission(privilege, menu.roles || '')

    const withSetting = hasSetting(setting, menu.setting || '')

    if (Array.isArray(subMenu) && subMenu.length) {
      menu.subMenu = subMenu.filter(checkPermission)
    }

    return withPermission && withSetting
  }

  // 过滤没有子菜单的父菜单项
  const filterEmptyMenu = (menu: MenuItemType): boolean => {
    const { subMenu } = menu
    if (Array.isArray(subMenu) && subMenu.length) {
      menu.subMenu = subMenu.filter(filterEmptyMenu)
    }
    return !menu.subMenu || Boolean(menu.subMenu.length)
  }

  // 这里必须先鉴权再过滤空菜单
  return menuList.filter(checkPermission).filter(filterEmptyMenu)
}

export const formatMenuList = ({
  menuList,
  sidebarTotalMap = {},
  sidebarUnreadMap = {},
  sidebarTodoTaskMap = {},
}: {
  menuList: MenuItemType[]
  sidebarUnreadMap?: UnreadMap
  sidebarTotalMap?: TotalMap
  sidebarTodoTaskMap?: TodoTaskMap
}) => {
  const { highlightMap, i18nPrefix } = shellConfig
  // 重组 menuList
  const recombineMenuList = (function loop(menus, parentMenu: string[] = []): FormatedMenuItemType[] {
    return menus.map((menu) => {
      const { id, subMenu = null, url, system, redirect } = menu

      // 空字符串也是有 URL 的情况，相当于 '/'
      const hasUrl = typeof url === 'string'
      const urlHeader = system && HOSTS[system]

      // 菜单未读数
      const unreadCount = sidebarUnreadMap[id] ?? 0

      const total = sidebarTotalMap[id] ?? 0

      // 工作事项待办数
      const todoTaskCount = sidebarTodoTaskMap[id] ?? 0

      // 各菜单的 URL 加上对应系统的 base URL 组成完整的 URL
      const urlWithHeader = `${urlHeader}${url}`
      let formatedRedirect
      if (redirect) {
        formatedRedirect = {
          ...redirect,
          url: `${HOSTS[redirect.system]}${redirect.url}`,
        }
      }

      return {
        ...menu,
        name: i18nHandle.t(`${i18nPrefix}menu.${id}`) + (total ? ` (${total})` : ''),
        subMenu: subMenu ? loop(subMenu, [...parentMenu, id]) : undefined,
        url: hasUrl && urlWithHeader,
        redirect: redirect && formatedRedirect,
        parentMenu,
        // 所有需要此菜单项高亮的 URL
        highlightUrls: (highlightMap[id] || []).concat(hasUrl ? urlWithHeader : []),
        unreadCount,
        total,
        todoTaskCount,
      } as FormatedMenuItemType
    })
  })(menuList)

  return recombineMenuList
}

export default {}

export function isSameUrlPath(compareWith: string, current?: string) {
  // 转成数组比较，为了处理类似 customer/personal/:id 的情况
  const currentPaths = (current || window.location.pathname).split('/').filter((pathItem) => pathItem)

  const compareWithPaths: string[] = compareWith
    .split('/')
    .filter((pathItem) => pathItem)
    .map((path, index) => {
      return path.startsWith(':') ? currentPaths[index] : path
    })
  return isEqual(currentPaths, compareWithPaths)
}

export function getLocalTimezone() {
  return -new Date().getTimezoneOffset() / 60
}

export const waitFewMoment = (few = 5) => {
  return new Promise<void>((resolve) => {
    nextTick(() =>
      window.requestAnimationFrame(() => {
        resolve(few > 1 ? waitFewMoment(few - 1) : void 0)
      }),
    )
  })
}
